/**
 * @file individualService.js
 * @description APIs for individual entity requests.
 **/
import { individualApiHttp, userApiHttp } from './httpService';
import qs from 'qs';
/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');
const getCompanyCode = () => window.localStorage.getItem('companyCode');

/**
 * Searches for individuals by various criteria.
 *
 * @param {Object} searchCriteria - The object containing the search criteria.
 * @param {string} [searchCriteria.search] - Free-text search term.
 * @param {number[]} [searchCriteria.populationGroups] - Population group IDs to filter by.
 * @param {number} [searchCriteria.page] - The page number (1-based).
 * @param {number} [searchCriteria.pageSize] - The number of items per page.
 * @param {number} [searchCriteria.sortBy] - An IndividualSearchSortField value.
 * @param {boolean} [searchCriteria.sortDescending] - Whether to sort descending.
 * @param {string[]} [searchCriteria.statuses] - Tenant user statuses to filter by.
 * @param {string[]} [searchCriteria.employmentCategories] - Employment categories to filter by.
 * @param {string[]} [searchCriteria.jobTitles] - Job titles to filter by.
 * @returns {Promise<Object>} - The response data ({ individuals, recordCount, statuses, employmentCategories, jobTitles }).
 * @throws {Error} - If there is an error performing the search.
 */
export const searchIndividuals = async searchCriteria => {
    try {
        const tenantId = getTenantId();

        const queryString = qs.stringify(
            { ...searchCriteria, tenantId },
            { arrayFormat: 'repeat' },
        );

        const response = await individualApiHttp.get(
            `v2/individual/search?${queryString}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error searching individuals:', error);
        throw error;
    }
};

/**
 * Retrieves an individual's record by their unique ID.
 *
 * @param {string} id - The ID of the individual.
 * @returns {Promise<Object>} - The response data with the individual's details.
 * @throws {Error} - If there is an error retrieving the individual record.
 */
export const getIndividualRecord = async id => {
    try {
        const response = await individualApiHttp.get(`/v2/individual/${id}`);
        return response.data;
        // return mockIndividualSearchResults.find((e) => e.uniqueId === uniqueId);
    } catch (error) {
        console.error('Error retrieving individual record:', error);
        throw error;
    }
};

/**
 * Deletes an entire Individual record by their unique ID.
 *
 * @param {int} individualId - The ID of the individual to delete.
 * @returns {Promise<void>} - A promise that resolves when the record is deleted.
 * @throws {Error} - If there is an error deleting the individual record.
 */
export const deleteIndividualRecord = async individualId => {
    try {
        await individualApiHttp.delete(`/v2/individual/${individualId}`);
    } catch (error) {
        console.error('Error deleting individual record:', error);
        throw error;
    }
};

/**
 * Retrieves enrollment details of an individual.
 *
 * @param {string} uniqueId - The unique ID of the individual.
 * @param {string} asOfDate - Date to get current enrollment data.
 * @param {string} planType - Type of plan to get enrollment history.
 * @returns {Promise<Object>} - The response data with the enrollment details.
 * @throws {Error} - If there is an error retrieving the enrollment information.
 */
export const getIndividualEnrollment = async (userId, payload) => {
    try {
        const response = await individualApiHttp.get(
            `/v2/individual/${userId}/enrollment`,
            payload,
        );
        return response.data.sort(
            (a, b) => a.planTypeSortOrder - b.planTypeSortOrder,
        );
    } catch (error) {
        console.error('Error retrieving individual enrollment data:', error);
        throw error;
    }
};

/**
 * Creates a new individual record with the provided demographic data.
 *
 * @param {Object} demographicData - The demographic data for the new individual.
 * @returns {Promise<Object>} - The response data with the created individual record.
 * @throws {Error} - If there is an error creating the individual record.
 */
export const createIndividual = async demographicData => {
    try {
        const response = await individualApiHttp.post('/v2/individual', {
            ...demographicData,
        });
        return response.data;
    } catch (error) {
        console.error('Error creating individual:', error);
        throw error;
    }
};

/**
 * Updates the demographic data of an individual by their unique ID.
 *
 * @param {string} uniqueId - The unique ID of the individual to update.
 * @param {Object} demographicData - The updated demographic data for the individual.
 * @returns {Promise<Object>} - The response data with the updated individual record.
 * @throws {Error} - If there is an error updating the individual record.
 */
export const updateIndividual = async demographicData => {
    try {
        const response = await individualApiHttp.put(`/v2/individual`, {
            ...demographicData,
        });
        return response.data;
    } catch (error) {
        console.error('Error updating individual demographic:', error);
        throw error;
    }
};

/**
 * Initiates the onboarding process for a user.
 *
 * @param {string} userId - The ID of the user.
 * @param {string} companyCode - The company code for the user.
 * @param {string} individualId - The individual ID of the user.
 * @returns {Promise<object | null>} A promise that resolves to the onboarding initiation status.
 */
export const initiateUserOnboarding = async (
    userId,
    email,
    mobile,
    companyCode = null,
) => {
    try {
        if (!companyCode) companyCode = getCompanyCode();
        const params = new URLSearchParams({ namespace: companyCode });
        if (email) params.append('email', email);
        if (mobile) params.append('mobile', mobile);
        const response = await userApiHttp.post(
            `/v2/user/onboarding/individual/send/${userId}?${params.toString()}`,
            {
                ...(mobile ? { mobile } : {}),
            },
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error initiating user onboarding:', error);
        throw error;
    }
};

/**
 * Initiates additional onboarding for a user.
 *
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Object>} A promise that resolves with the onboarding response data.
 */
export const initiateAdditionalUserOnboarding = async (
    userId,
    email,
    mobile,
    companyCode = null,
) => {
    try {
        const companyCode = getCompanyCode();
        if (!companyCode) {
            throw new Error('Company code is not available in local storage.');
        }
        const url = `/v2/user/onboarding/additional/send/${userId}`;
        const response = await userApiHttp.post(
            `${url}?namespace=${companyCode}&email=${email}&mobile=${mobile}`,
            {
                ...(mobile ? { mobile: mobile } : {}),
            },
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error initiating additional user onboarding:', error);
        throw error;
    }
};

/**
 * Initiates the onboarding process for a multi-tenant user.
 *
 * @param {string} userId - The ID of the user.
 * @param {string} companyCode - The company code for the user.
 * @returns {Promise<object | null>} A promise that resolves to the onboarding initiation status.
 */
export const initiateMultitenantUserOnboarding = async (
    userId,
    email,
    mobile,
    companyCode,
) => {
    try {
        if (!companyCode) companyCode = getCompanyCode();
        const response = await userApiHttp.post(
            `/v2/user/onboarding/admin/send/${userId}?namespace=${companyCode}&email=${email}&mobile=${mobile}`,
            {},
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error initiating user onboarding:', error);
        throw error;
    }
};

/**
 * Retrieves the preferences for a specific user.
 *
 * @param {string} individualID - The ID of the individual.
 * @param {string} tenantUserID - The ID of the tenant user.
 * @returns {Promise<object>} A promise that resolves to the user's preferences data.
 * @throws {Error} - If there is an error retrieving the preferences.
 */
export const getUserPreferences = async (individualID, tenantUserID) => {
    try {
        const response = await individualApiHttp.get(
            `/v2/individual/${individualID}/user/${tenantUserID}/preferences`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving user preferences:', error);
        throw error;
    }
};

export const getMessageTypes = async () => {
    const response = await individualApiHttp.get('v2/individual/message-types');
    return response.data;
};

/**
 * Updates the communication preferences for a user
 * @async
 * @param {Object} preferences - The communication preferences object
 * @returns {Promise<Object>} The response data from the server
 * @throws {Error} If the request fails
 */
export const updateCommunicationPreferences = async (
    individualID,
    tenantUserID,
    preferences,
) => {
    const response = await individualApiHttp.put(
        `/v2/individual/${individualID}/user/${tenantUserID}/preferences`,
        preferences,
    );
    return response.data;
};

/**
 * Creates a tenant user tied to an individual
 * @async
 * @param {Object} preferences - The communication preferences object
 * @returns {Promise<Object>} The response data from the server
 * @throws {Error} If the request fails
 */
export const createTenantUser = async (individualID, preferences) => {
    const response = await individualApiHttp.post(
        `/v2/individual/${individualID}/user`,
        preferences,
    );
    return response.data;
};

/**
 * Resets a user's account settings.
 * @param {string} tenantUserID - The ID of the tenant user.
 * @returns {Promise<Object>} A promise that resolves with the response data.
 * @throws {Error} If there is an error resetting the user.
 */
export const resetUser = async (individualID, tenantUserID) => {
    try {
        const response = await individualApiHttp.post(
            `/v2/individual/${individualID}/user/${tenantUserID}/reset`,
        );
        return response.data;
    } catch (error) {
        console.error('Error resetting user:', error);
        throw error;
    }
};

/**
 * Retrieves all data point categories for the current tenant.
 *
 * @returns {Promise<Array>} A promise that resolves with the list of data point categories.
 * @throws {Error} If there is an error retrieving the categories.
 */
export const getDataPointCategories = async () => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.get(
            `/v2/individual/tenant/${tenantId}/data-point-category`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching data point categories:', error);
        throw error;
    }
};

/**
 * Creates a new data point category for the current tenant.
 *
 * @param {Object} params - The category parameters.
 * @param {string} params.name - The display name for the new category.
 * @param {string} params.code - The code for the new category.
 * @returns {Promise<Object>} A promise that resolves with the created category.
 * @throws {Error} If there is an error creating the category.
 */
export const createDataPointCategory = async ({ name, code }) => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.post(
            `/v2/individual/tenant/${tenantId}/data-point-category`,
            { id: 0, tenantId, name, code },
        );
        return response.data;
    } catch (error) {
        console.error('Error creating data point category:', error);
        throw error;
    }
};

/**
 * Updates an existing data point category.
 *
 * @param {Object} params - The category parameters.
 * @param {number} params.id - The ID of the category to update.
 * @param {string} params.name - The updated display name for the category.
 * @param {string} params.code - The updated code for the category.
 * @param {boolean} params.enabled - Whether the category is enabled.
 * @returns {Promise<Object>} A promise that resolves with the updated category.
 * @throws {Error} If there is an error updating the category.
 */
export const updateDataPointCategory = async ({ id, name, code, enabled }) => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.put(
            `/v2/individual/tenant/${tenantId}/data-point-category`,
            { id, tenantId, name, code, enabled },
        );
        return response.data;
    } catch (error) {
        console.error('Error updating data point category:', error);
        throw error;
    }
};

/**
 * Deletes a data point category by its ID.
 *
 * @param {number} id - The ID of the category to delete.
 * @returns {Promise<Object>} A promise that resolves when the category is deleted.
 * @throws {Error} If there is an error deleting the category.
 */
export const deleteDataPointCategory = async id => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.delete(
            `/v2/individual/tenant/${tenantId}/data-point-category/${id}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting data point category:', error);
        throw error;
    }
};

/**
 * Retrieves data point subcategories for the current tenant, optionally filtered by category IDs.
 *
 * @param {number[]} [categoryIds=[]] - Optional list of category IDs to filter subcategories by.
 * @returns {Promise<Array>} A promise that resolves with the list of data point subcategories.
 * @throws {Error} If there is an error retrieving the subcategories.
 */
export const getDataPointSubCategories = async (categoryIds = []) => {
    const tenantId = getTenantId();
    try {
        const queryString =
            categoryIds.length > 0
                ? qs.stringify({ categoryIds }, { arrayFormat: 'repeat' })
                : null;
        const url = `/v2/individual/tenant/${tenantId}/data-point-subcategory${queryString ? `?${queryString}` : ''}`;
        const response = await individualApiHttp.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching data point sub-categories:', error);
        throw error;
    }
};

/**
 * Creates a new data point subcategory.
 *
 * @param {Object} params - The subcategory parameters.
 * @param {string} params.name - The display name for the new subcategory.
 * @param {string} params.code - The code for the new subcategory.
 * @param {number} params.dataPointCategoryId - The ID of the parent category.
 * @returns {Promise<Object>} A promise that resolves with the created subcategory.
 * @throws {Error} If there is an error creating the subcategory.
 */
export const createDataPointSubCategory = async ({
    name,
    code,
    dataPointCategoryId,
}) => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.post(
            `/v2/individual/tenant/${tenantId}/data-point-subcategory`,
            { id: 0, dataPointCategoryId, name, code },
        );
        return response.data;
    } catch (error) {
        console.error('Error creating data point sub-category:', error);
        throw error;
    }
};

/**
 * Updates an existing data point subcategory.
 *
 * @param {Object} params - The subcategory parameters.
 * @param {number} params.id - The ID of the subcategory to update.
 * @param {string} params.name - The updated display name for the subcategory.
 * @param {string} params.code - The updated code for the subcategory.
 * @param {number} params.dataPointCategoryId - The ID of the parent category.
 * @param {boolean} params.enabled - Whether the subcategory is enabled.
 * @returns {Promise<Object>} A promise that resolves with the updated subcategory.
 * @throws {Error} If there is an error updating the subcategory.
 */
export const updateDataPointSubCategory = async ({
    id,
    name,
    code,
    dataPointCategoryId,
    enabled,
}) => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.put(
            `/v2/individual/tenant/${tenantId}/data-point-subcategory`,
            { id, dataPointCategoryId, name, code, enabled },
        );
        return response.data;
    } catch (error) {
        console.error('Error updating data point sub-category:', error);
        throw error;
    }
};

/**
 * Deletes a data point subcategory by its ID.
 *
 * @param {number} id - The ID of the subcategory to delete.
 * @returns {Promise<Object>} A promise that resolves when the subcategory is deleted.
 * @throws {Error} If there is an error deleting the subcategory.
 */
export const deleteDataPointSubCategory = async id => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.delete(
            `/v2/individual/tenant/${tenantId}/data-point-subcategory/${id}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting data point sub-category:', error);
        throw error;
    }
};

/**
 * Retrieves all data points for the current tenant across all individuals.
 *
 * @returns {Promise<Array>} A promise that resolves with the list of data points.
 * @throws {Error} If there is an error retrieving the data points.
 */
export const getDataPoints = async () => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.get(
            `/v2/individual/tenant/${tenantId}/data-point`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching data point categories:', error);
        throw error;
    }
};

/**
 * Retrieves all data points for a specific individual within the current tenant.
 *
 * @param {number} individualId - The ID of the individual whose data points to retrieve.
 * @returns {Promise<Array>} A promise that resolves with the list of data points for the individual.
 * @throws {Error} If there is an error retrieving the data points.
 */
export const getDataPointsByIndividualId = async individualId => {
    const tenantId = getTenantId();
    try {
        const response = await individualApiHttp.get(
            `/v2/individual/tenant/${tenantId}/data-point/individual/${individualId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching data point categories:', error);
        throw error;
    }
};
